import { CurrencyPipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslocoPipe } from '@jsverse/transloco';
import { PositiveAndInputLowerThanDirective } from '@zero/shared/ui-directives';
import { NumberInputComponent, TransactionAuthorizationComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { YearInputValidatorDirective } from '@zero/shared/util-form-validators';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { TicketFacade } from '@zero/tickets/domain';
import { environment } from 'environments/environment';

@Component({
  selector: 'self-service-change-fsa',
  templateUrl: './change-fsa.component.html',
  providers: [UserMessagesHolderService],
  imports: [
    FormsModule,
    NumberInputComponent,
    PositiveAndInputLowerThanDirective,
    TransactionAuthorizationComponent,
    CurrencyPipe,
    TranslocoPipe,
    YearInputValidatorDirective,
    ButtonComponent,
  ],
})
export class ChangeFsaComponent implements OnInit {
  @Input() public existingFsaAmount?: number;
  @Input() public existingFsaUsedAmount?: number;
  @Input() public customerId?: string;
  @Input() public maritalStatus?: string;
  @Input() public dialogMode?: 'initial' | 'checkprecondition' | 'openChange' | 'edit' | 'verify' | 'submitted';

  @ViewChild('tanInput') public tanInput: ElementRef;

  public readonly ATTR_NEW_FSA_AMOUT = TicketData.ATTR_NEW_FSA_AMOUT;
  public readonly ATTR_NEW_FSA_VALIDFROM = TicketData.ATTR_NEW_FSA_VALIDFROM;
  public readonly ATTR_NEW_FSA_VALIDTO = TicketData.ATTR_NEW_FSA_VALIDTO;
  public checkValidTo = true;
  public dataToAuthorize?: AuthorizationData<AuthPayloadTicket>;
  public previewSeen = false;
  public validFromMaxYear?: number;
  public validFromMinYear?: number;
  public editData = {};
  public pdfUrl: SafeUrl;

  #ticket?: Ticket;
  readonly #ticketFacade = inject(TicketFacade);
  readonly #messageService = inject(UserMessagesHolderService);
  readonly #sanitizer = inject(DomSanitizer);

  public ngOnInit(): void {
    this.#messageService.clearAllMessages();
    this.#initDateRanges();
    this.editData[this.ATTR_NEW_FSA_VALIDFROM] = `${this.validFromMinYear}`;
  }

  #isBeforeDeadline(): boolean {
    const nowYearUTC = new Date().getUTCFullYear();
    const deadlineUTC = Date.UTC(nowYearUTC, 11, 31, 22, 59, 59);
    return Date.now() < deadlineUTC;
  }

  #initDateRanges(): void {
    const today = new Date();
    this.validFromMinYear = this.#isBeforeDeadline() ? today.getFullYear() : today.getFullYear() + 1;
    this.validFromMaxYear = this.#isBeforeDeadline() ? today.getFullYear() + 1 : today.getFullYear() + 2;
  }

  public checkValidToChanged(): void {
    if (!this.checkValidTo && !this.editData[this.ATTR_NEW_FSA_VALIDTO]) {
      this.editData[this.ATTR_NEW_FSA_VALIDTO] = this.editData[this.ATTR_NEW_FSA_VALIDFROM];
    }
    if (this.checkValidTo) {
      delete this.editData[this.ATTR_NEW_FSA_VALIDTO];
    }

    this.inputValueChanged();
  }

  public fsaMinValue(validFromYear: string): number {
    const isCurrentYear = `${validFromYear}` === `${new Date().getFullYear()}`;
    return isCurrentYear ? this.existingFsaUsedAmount : 0;
  }

  public onSubmit(f: NgForm): void {
    if (this.dialogMode === 'edit') {
      this.#startVerification(f);
    }
  }

  #startVerification(f: NgForm): void {
    if (f.valid) {
      this.#messageService.clearAllMessages();
      this.#ticket = new Ticket();
      this.#ticket.customerId = this.customerId;
      this.#ticket.type = 'FSA_VIATAN';

      this.#ticket.ticketData = new TicketData();
      this.#ticket.ticketData.additionalProperties = this.prepareEditData();
      this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(this.#ticket));
      this.dialogMode = 'verify';
    }
  }

  public prepareEditData(): any {
    const ret = {};

    // we want the amount as string
    ret[TicketData.ATTR_NEW_FSA_AMOUT] = `${this.editData[TicketData.ATTR_NEW_FSA_AMOUT]}`;

    ret[TicketData.ATTR_NEW_FSA_VALIDFROM] = this.editData[TicketData.ATTR_NEW_FSA_VALIDFROM]
      ? `${this.editData[TicketData.ATTR_NEW_FSA_VALIDFROM]}`
      : '';
    ret[TicketData.ATTR_NEW_FSA_VALIDTO] = this.editData[TicketData.ATTR_NEW_FSA_VALIDTO]
      ? `${this.editData[TicketData.ATTR_NEW_FSA_VALIDTO]}`
      : '';

    return ret;
  }

  #createPreviewPdfUrl(): void {
    const fsaData = this.dialogMode === 'verify' ? this.#ticket.ticketData.additionalProperties : this.prepareEditData();
    const amount = fsaData[TicketData.ATTR_NEW_FSA_AMOUT];
    if (amount === 'undefined') {
      return;
    }

    const url = `${environment.apihost + environment.changeFsaPreviewPdfUrl}/Freistellungsauftrag.pdf?`;
    const params = new HttpParams()
      .append('fsaAmount', amount)
      .append('fsaValidFrom', fsaData[TicketData.ATTR_NEW_FSA_VALIDFROM])
      .append('fsaValidTo', fsaData[TicketData.ATTR_NEW_FSA_VALIDTO]);

    this.pdfUrl = this.#sanitizer.bypassSecurityTrustUrl(url + params.toString());
  }

  public inputValueChanged(): void {
    this.previewSeen = false;
    this.#createPreviewPdfUrl();
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.#messageService.clearAllMessages();
      this.#messageService.addInfoMessage(
        'Die Autorisierung war erfolgreich. Dein Freistellungsauftrag wird in den nächsten Tagen geändert, ein Service-Ticket wurde dazu erstellt.',
        true,
      );
      this.dataToAuthorize = undefined;
      this.#ticketFacade.loadTickets(true);
    } else if (result.reason === 'CanceledByUser') {
      this.dialogMode = 'initial';
      this.dataToAuthorize = undefined;
    }
  }
}
